import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Button, Divider,
    useTheme, useMediaQuery, Paper, TextField
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import HistoryIcon from '@mui/icons-material/History';
import EditNoteIcon from '@mui/icons-material/EditNote';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import MapIcon from '@mui/icons-material/Map';

const TaskDetailView = ({ operation, onSave, onRedirect, onViewOnMap, canEdit = true }) => {
    const theme = useTheme();
    const [isEditing, setIsEditing] = useState(false);
    const [editedDescription, setEditedDescription] = useState('');

    useEffect(() => {
        if (operation) {
            setEditedDescription(operation.description || '');
            setIsEditing(false);
        }
    }, [operation]);

    if (!operation) return null;

    const handleSave = () => {
        onSave({
            ...operation,
            description: editedDescription
        });
        setIsEditing(false);
    };

    const handleCancel = () => {
        setEditedDescription(operation.description || '');
        setIsEditing(false);
    };

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 3, pb: 3 }}>
            {/* Header Section */}
            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    borderRadius: 3,
                    bgcolor: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    position: 'relative',
                    overflow: 'hidden'
                }}
            >
                <Box sx={{
                    position: 'absolute',
                    top: -20,
                    right: -20,
                    width: 100,
                    height: 100,
                    borderRadius: '50%',
                    bgcolor: '#1e3a8a',
                    opacity: 0.05
                }} />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <Box sx={{
                        p: 1,
                        borderRadius: 1.5,
                        bgcolor: 'rgba(30, 58, 138, 0.1)',
                        color: '#1e3a8a',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <NotificationsActiveIcon fontSize="small" />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e3a8a', letterSpacing: -0.5 }}>
                        Task Details
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <CalendarTodayIcon sx={{ fontSize: 16, color: '#64748b' }} />
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
                            {operation.date}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <AccessTimeIcon sx={{ fontSize: 16, color: '#64748b' }} />
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
                            {operation.start_time} {operation.timezone || ''}
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            {/* Content Section */}
            <Box sx={{ flex: 1, px: 1 }}>
                <Typography
                    variant="overline"
                    sx={{
                        color: '#64748b',
                        fontWeight: 700,
                        fontSize: '0.75rem',
                        letterSpacing: 1,
                        display: 'block',
                        mb: 1.5
                    }}
                >
                    {isEditing ? 'Editing Description' : 'Description & Notes'}
                </Typography>

                {isEditing ? (
                    <TextField
                        fullWidth
                        multiline
                        rows={8}
                        value={editedDescription}
                        onChange={(e) => setEditedDescription(e.target.value)}
                        placeholder="Type task details..."
                        variant="outlined"
                        autoFocus
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                bgcolor: '#fff',
                                backgroundColor: '#fff',
                            }
                        }}
                    />
                ) : (
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2.5,
                            borderRadius: 2,
                            bgcolor: '#fff',
                            border: '1px solid #e2e8f0',
                            minHeight: 200,
                            whiteSpace: 'pre-wrap',
                            color: '#1e293b',
                            lineHeight: 1.6,
                            fontSize: '0.95rem'
                        }}
                    >
                        {operation.description || 'No description provided for this task.'}
                    </Paper>
                )}
            </Box>

            {/* Actions Section */}
            <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
                {isEditing ? (
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                            variant="outlined"
                            fullWidth
                            onClick={handleCancel}
                            startIcon={<CloseIcon />}
                            sx={{
                                py: 1.5,
                                borderRadius: 2.5,
                                fontWeight: 700,
                                textTransform: 'none',
                                color: '#64748b',
                                borderColor: '#e2e8f0'
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            fullWidth
                            onClick={handleSave}
                            startIcon={<SaveIcon />}
                            sx={{
                                bgcolor: '#2e7d32',
                                py: 1.5,
                                borderRadius: 2.5,
                                fontWeight: 700,
                                textTransform: 'none',
                                '&:hover': { bgcolor: '#1b5e20' }
                            }}
                        >
                            Save Data
                        </Button>
                    </Box>
                ) : (
                    canEdit && (
                        <Button
                            variant="contained"
                            fullWidth
                            onClick={() => setIsEditing(true)}
                            startIcon={<EditNoteIcon />}
                            sx={{
                                bgcolor: '#1e3a8a',
                                py: 1.5,
                                borderRadius: 2.5,
                                fontWeight: 700,
                                textTransform: 'none',
                                fontSize: '1rem',
                                boxShadow: '0 4px 12px rgba(30, 58, 138, 0.2)',
                                '&:hover': {
                                    bgcolor: '#172554',
                                    boxShadow: '0 6px 16px rgba(30, 58, 138, 0.3)',
                                }
                            }}
                        >
                            Edit Task Description
                        </Button>
                    )
                )}

                {!isEditing && onRedirect && (
                    <Button
                        variant="outlined"
                        fullWidth
                        onClick={onRedirect}
                        startIcon={<HistoryIcon />}
                        sx={{
                            color: '#64748b',
                            borderColor: '#e2e8f0',
                            py: 1.25,
                            borderRadius: 2.5,
                            fontWeight: 600,
                            textTransform: 'none',
                            '&:hover': {
                                borderColor: '#cbd5e1',
                                bgcolor: '#f8fafc'
                            }
                        }}
                    >
                        View in Mission Log
                    </Button>
                )}

                {!isEditing && onViewOnMap && (
                    <Button
                        variant="contained"
                        fullWidth
                        onClick={() => onViewOnMap(operation)}
                        startIcon={<MapIcon />}
                        sx={{
                            bgcolor: '#0ea5e9',
                            py: 1.25,
                            borderRadius: 2.5,
                            fontWeight: 600,
                            textTransform: 'none',
                            '&:hover': {
                                bgcolor: '#0284c7'
                            }
                        }}
                    >
                        View on Map (Beta)
                    </Button>
                )}
            </Box>
        </Box>
    );
};

export default TaskDetailView;
